/**
 * StageCard — collapsible card primitive for the setup-master wizard.
 *
 * Each of the 6 wizard stages renders one StageCard. The card has:
 * - header: status icon + title + summary + chevron
 * - body (when expanded): stage-specific content + primary CTA
 *
 * Default expansion rule per design doc §4:
 *   - `in_progress` / `error`: expanded (operator needs the action surface)
 *   - `done`: collapsed (just a check mark and summary)
 *   - `pending` / `blocked`: collapsed (operator can't act yet)
 *
 * The status icon → Lucide mapping:
 *   done         → CheckCircle2  (emerald)
 *   in_progress  → Loader2       (animate-spin, primary)
 *   blocked      → Pause         (muted)
 *   error        → AlertTriangle (destructive)
 *   pending      → Circle        (muted-foreground)
 */
import { useState, type ReactNode } from 'react';
import {
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Circle,
  Loader2,
  Pause,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { useT } from '@/contexts/I18nContext';
import type { StageStatus } from '@/hooks/useWizardState';

interface Props {
  /** Stage number rendered as a leading numeral (1..6). */
  number: number;
  /** Translated stage title. */
  title: string;
  /** Translated one-line summary shown to the right of the header. */
  summary?: string;
  status: StageStatus;
  /** Blocked-state reason (i18n key); rendered inside the body when expanded. */
  blockedReasonKey?: string;
  /** Error diagnostic (already translated/plain text); rendered when status=error. */
  errorDetail?: string;
  /** Expanded body — typically the stage's adapter component + primary CTA. */
  children?: ReactNode;
}

export function StageCard({
  number,
  title,
  summary,
  status,
  blockedReasonKey,
  errorDetail,
  children,
}: Props) {
  const { t } = useT();
  const [expanded, setExpanded] = useState(defaultExpanded(status));
  const Icon = statusIcon(status);

  return (
    <Card>
      <CardContent className="p-0">
        <button
          type="button"
          onClick={() => setExpanded(v => !v)}
          className="flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-muted/30"
          aria-expanded={expanded}
        >
          <span className="flex size-7 shrink-0 items-center justify-center rounded-full bg-muted text-xs font-semibold tabular-nums text-muted-foreground">
            {number}
          </span>
          <Icon
            className={cn('size-5 shrink-0', iconColorClass(status), status === 'in_progress' && 'animate-spin')}
          />
          <span className="flex-1 truncate text-sm font-semibold">{title}</span>
          {summary ? (
            <span className="hidden truncate text-xs text-muted-foreground sm:inline">
              {summary}
            </span>
          ) : null}
          {expanded ? (
            <ChevronDown className="size-4 shrink-0 text-muted-foreground" />
          ) : (
            <ChevronRight className="size-4 shrink-0 text-muted-foreground" />
          )}
        </button>
        {expanded && (
          <div className="space-y-3 border-t border-border bg-card/30 px-4 py-3">
            {status === 'blocked' && blockedReasonKey && (
              <div className="rounded-md border border-amber-300/40 bg-amber-50/60 px-3 py-2 text-xs text-amber-900 dark:border-amber-700/40 dark:bg-amber-950/30 dark:text-amber-200">
                {t(blockedReasonKey)}
              </div>
            )}
            {status === 'error' && errorDetail && (
              <div className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
                {errorDetail}
              </div>
            )}
            {children}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ── helpers ─────────────────────────────────────────────────────────

function statusIcon(s: StageStatus) {
  switch (s) {
    case 'done':         return CheckCircle2;
    case 'in_progress':  return Loader2;
    case 'blocked':      return Pause;
    case 'error':        return AlertTriangle;
    case 'pending':      return Circle;
  }
}

function iconColorClass(s: StageStatus): string {
  switch (s) {
    case 'done':         return 'text-emerald-600';
    case 'in_progress':  return 'text-primary';
    case 'blocked':      return 'text-muted-foreground';
    case 'error':        return 'text-destructive';
    case 'pending':      return 'text-muted-foreground/60';
  }
}

function defaultExpanded(s: StageStatus): boolean {
  return s === 'in_progress' || s === 'error';
}
